<script>
  import { onMount, onDestroy } from 'svelte';
  import { selectedOrgId, isCrossTenantOrgContext } from '../../lib/orgContext.js';
  import {
    backtestResult, backtestRunning, backtestError, selectedRunId, backtestStatus, loadingRun,
  } from '../../lib/store.js';
  import { backtestProgress } from '../../lib/sseStore.js';
  import { strategyById } from '../../lib/strategies.js';
  import { isResultMeaningful } from '../../lib/strategyResult.js';
  import { loadRunInto } from '../../lib/backtestLoad.js';
  import api from '../../lib/api.js';

  import BacktestForm from './BacktestForm.svelte';
  import BacktestProgress from './BacktestProgress.svelte';
  import BacktestResults from './BacktestResults.svelte';
  import RunParametersPanel from './RunParametersPanel.svelte';
  import RunHistory from './RunHistory.svelte';
  import EquityCurveChart from '../shared/charts/EquityCurveChart.svelte';
  import DrawdownChart from '../shared/charts/DrawdownChart.svelte';
  import ReturnsChart from '../shared/charts/ReturnsChart.svelte';
  // Tear-sheet charts (U8/U9) — rendered AFTER the existing 3, reactively from the same response's
  // `tearsheet` (no second fetch). The datasets arrive ready-to-plot (KTD4).
  import RollingVolatilityChart from '../shared/charts/RollingVolatilityChart.svelte';
  import RollingSharpeChart from '../shared/charts/RollingSharpeChart.svelte';
  import UnderwaterChart from '../shared/charts/UnderwaterChart.svelte';
  import AnnualReturnsChart from '../shared/charts/AnnualReturnsChart.svelte';
  import CumulativeVsBenchmarkChart from '../shared/charts/CumulativeVsBenchmarkChart.svelte';
  import DrawdownPeriodsTable from '../shared/charts/DrawdownPeriodsTable.svelte';
  import MonthlyReturnsHeatmap from '../shared/charts/MonthlyReturnsHeatmap.svelte';
  import ReturnDistributionChart from '../shared/charts/ReturnDistributionChart.svelte';
  import ReturnQuantilesChart from '../shared/charts/ReturnQuantilesChart.svelte';

  let formComponent;
  let historyComponent;
  let historyVisible = true;   // Run-history panel shown by default; toggled from the header icon button.
  let channelId = null;
  let lastParams = null;     // the request of the most recent live run, for "New backtest" prefill
  let runSummary = null;      // a compact summary shown when results are open
  let orgSwitchWarning = null;
  let runStartOrgId = null;   // org the in-flight run was launched under
  let activeRunId = null;     // the enqueued background run being tracked (TASK-20260728)
  let pollTimer = null;       // ~10s status-poll fallback (SSE has no replay — a gap can eat the terminal)
  let finishing = false;      // re-entrancy guard: SSE 'completed' and the poll can race
  let queuedWaiting = false;  // poll saw status 'queued' — the run hasn't started yet (review W9)

  // The in-flight run's identity, persisted so recovery works across BOTH remounts (module state is
  // gone) and full reloads (review W2/W3) — and so recovery never adopts some OTHER run in the
  // currently-selected org by guesswork.
  const ACTIVE_RUN_KEY = 'k2.activeBacktestRun';
  function rememberActiveRun(runId, orgId) {
    try { localStorage.setItem(ACTIVE_RUN_KEY, JSON.stringify({ runId, orgId: orgId ?? null })); } catch { /* private mode */ }
  }
  function forgetActiveRun() {
    try { localStorage.removeItem(ACTIVE_RUN_KEY); } catch { /* private mode */ }
  }
  function recallActiveRun() {
    try { return JSON.parse(localStorage.getItem(ACTIVE_RUN_KEY) ?? 'null'); } catch { return null; }
  }

  // A non-control, <=100-char correlation id, unique within this user's runs (timestamp + counter).
  let runCounter = 0;
  function makeChannelId() {
    runCounter += 1;
    return `bt-${Date.now().toString(36)}-${runCounter}`;
  }

  $: meaningful = isResultMeaningful($backtestResult);
  // The tear-sheet datasets ride the SAME response (no second call). Keyed to the current run so a new
  // backtest replaces the prior tearsheet rather than leaving stale charts (U8). Null/absent → the new
  // charts section is hidden (the existing 3 + table are unaffected).
  $: tearsheet = $backtestResult?.tearsheet ?? null;

  // Drop a finished run's entry from the demux map so it doesn't accumulate over a long session.
  function clearProgress(id) {
    if (!id) return;
    backtestProgress.update((m) => {
      if (!m.has(id)) return m;
      const next = new Map(m);
      next.delete(id);
      return next;
    });
  }

  // TASK-20260728/ADR-0018: the run is a BACKGROUND job. Enqueue returns 202 { runId } immediately
  // (no HTTP request rides out the collection, so run length can never time out the UI); progress
  // streams over SSE and the terminal 'completed' triggers the result fetch, with a ~10s status poll
  // as the fallback signal.
  async function handleRun(event) {
    if ($backtestRunning) return; // single-run-at-a-time (re-entry disabled; the server enforces it too — 409)
    if ($isCrossTenantOrgContext) return; // another org's context is read-only (the form explains; the server 403s)
    const request = event.detail;
    const myChannel = makeChannelId();
    channelId = myChannel;
    lastParams = request;
    runStartOrgId = $selectedOrgId;
    activeRunId = null;
    runSummary = {
      strategy: strategyById(request.Strategy)?.name ?? 'Strategy',
      symbols: request.Symbols,
    };

    backtestError.set(null);
    backtestResult.set(null);
    selectedRunId.set(null);
    loadingRun.set(null); // a new live run cancels any pending history-load overlay
    backtestRunning.set(true);

    try {
      const { runId } = await api.enqueueBacktest({ ...request, ProgressCorrelationId: myChannel }, runStartOrgId);
      // Freshness guard: if this run was superseded while enqueueing, stop tracking it here — the run
      // still executes server-side and shows up in the run-start org's history.
      if (channelId !== myChannel) return;
      activeRunId = runId;
      rememberActiveRun(runId, runStartOrgId);
      historyComponent?.reload(); // the queued row appears in history immediately
      startPolling(myChannel, runId);
    } catch (err) {
      if (channelId === myChannel) {
        backtestError.set(mapRunError(err));
        backtestRunning.set(false);
        channelId = null;
        activeRunId = null;
      }
      clearProgress(myChannel);
    }
  }

  // The enqueue call is fast (no run work happens in it) — an abort here means the server is
  // unreachable, not that the run is long (U7).
  function mapRunError(err) {
    if (err?.name === 'AbortError') {
      return 'Could not reach the server to start the backtest. Please try again.';
    }
    // fetchWithErrorHandling throws Error("HTTP error! status: N, details: <body>"); surface the body.
    const msg = String(err?.message ?? err);
    const idx = msg.indexOf('details: ');
    return idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
  }

  // Terminal 'completed' for the active channel → fetch the persisted result by run id. (Terminal
  // events ride a backpressure-immune path, but SSE has no replay — the poll below covers a
  // reconnect gap that swallows this.)
  $: onProgressEntry(channelId ? $backtestProgress.get(channelId) : null);
  function onProgressEntry(entry) {
    if (!entry || !$backtestRunning || finishing) return;
    queuedWaiting = false; // any SSE event means the run is executing
    if (entry.phase === 'completed') {
      // Fast-completion race (review W4): the terminal can beat the 202 response; the payload
      // carries the run id, so never finish against a null activeRunId.
      if (!activeRunId && entry.runId) activeRunId = entry.runId;
      if (activeRunId) finishRun('done');
    }
  }

  // The ~10s fallback: reads the run's status from the history list (it carries queued/running/failed
  // rows). Also the ONLY signal after a reload/remount, where the SSE correlation id is lost.
  function startPolling(myChannel, runId) {
    stopPolling();
    pollTimer = setInterval(async () => {
      if (channelId !== myChannel || finishing) { stopPolling(); return; }
      try {
        const runs = await api.getBacktestRuns(runStartOrgId);
        const row = runs?.find((r) => r.runId === runId);
        if (!row) return;
        const status = String(row.status ?? '').toLowerCase();
        queuedWaiting = status === 'queued'; // surface "waiting to start" honestly (review W9)
        if (status === 'done') finishRun('done');
        else if (status === 'failed') finishRun('failed');
      } catch {
        // Transient poll failure — SSE remains the primary signal; the next tick retries.
      }
    }, 10_000);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function finishRun(status) {
    if (finishing) return;
    finishing = true;
    const myChannel = channelId;
    const runId = activeRunId;
    stopPolling();
    try {
      if (status === 'failed') {
        handleTerminalFailed();
        return;
      }
      const result = await api.getBacktestRun(runId, runStartOrgId);
      if (channelId === myChannel) {
        backtestResult.set(result);
        selectedRunId.set(runId);
      }
      historyComponent?.reload();
    } catch {
      if (channelId === myChannel) {
        backtestError.set('The run finished but the result could not be loaded. Check your run history.');
      }
    } finally {
      clearProgress(myChannel);
      forgetActiveRun();
      if (channelId === myChannel) {
        backtestRunning.set(false);
        channelId = null;
        activeRunId = null;
        queuedWaiting = false;
      }
      finishing = false;
    }
  }

  // A terminal SSE 'failed' (or a failed status from the poll) leaves Running with an error. The
  // channel is invalidated so any stale store writes for this run are dropped.
  function handleTerminalFailed() {
    if (!$backtestRunning) return;
    stopPolling();
    clearProgress(channelId);
    forgetActiveRun();
    channelId = null; // invalidate the in-flight run's pending store writes
    activeRunId = null;
    queuedWaiting = false;
    if (!$backtestError) {
      backtestError.set('The backtest failed. See details below or try a different configuration.');
    }
    backtestRunning.set(false);
  }

  // Reload/remount recovery (AC12, reworked per review W2/W3): recover ONLY the run this browser
  // enqueued (identity + org from localStorage) — never adopt some other member's run in whatever org
  // happens to be selected. Covers all three cases: still in flight (re-attach in progress-lite mode —
  // the old SSE correlation id is gone, the poll carries it to terminal), finished while the view was
  // unmounted (load the result; without this the module-level running flag sticks forever), and
  // failed/deleted (surface + clear).
  onMount(async () => {
    const tracked = recallActiveRun();
    if (!tracked?.runId) {
      // Nothing tracked but the flag stuck from a torn-down run (shouldn't happen once tracking is
      // authoritative) — reset so the form isn't dead.
      if ($backtestRunning) backtestRunning.set(false);
      return;
    }

    try {
      const runs = await api.getBacktestRuns(tracked.orgId);
      const row = runs?.find((r) => r.runId === tracked.runId);
      const status = String(row?.status ?? '').toLowerCase();
      runStartOrgId = tracked.orgId;

      if (status === 'queued' || status === 'running') {
        runSummary = { strategy: row.strategy ?? 'Strategy', symbols: row.symbols ?? '' };
        channelId = makeChannelId(); // local tracking token only — no SSE events will carry it
        activeRunId = tracked.runId;
        queuedWaiting = status === 'queued';
        backtestRunning.set(true);
        startPolling(channelId, tracked.runId);
        return;
      }

      forgetActiveRun();
      if ($backtestRunning) backtestRunning.set(false);
      if (status === 'done' && !$backtestResult) {
        const result = await api.getBacktestRun(tracked.runId, tracked.orgId);
        backtestResult.set(result);
        selectedRunId.set(tracked.runId);
        runSummary = { strategy: row.strategy ?? 'Strategy', symbols: row.symbols ?? '' };
      } else if (status === 'failed') {
        backtestError.set('Your last backtest failed. See run history for details.');
      }
    } catch {
      // Recovery is best-effort — the view still works; the run stays visible in history.
    }
  });

  onDestroy(stopPolling);

  function newBacktest() {
    backtestResult.set(null);
    backtestError.set(null);
    selectedRunId.set(null);
    loadingRun.set(null);
    runSummary = null;
    // Prefill the previous run's params (institutional iterate-one-knob pattern).
    if (lastParams) formComponent?.applyParams(lastParams);
  }

  // TASK-20260728: download the selected completed run's Export ZIP (returns/trades/metrics/README),
  // mirroring the Paper/Live per-strategy export. Scoped to the selected org like every backtest read.
  let exportBusy = false;
  async function onDownloadBacktestZip() {
    if (exportBusy || !$selectedRunId) return;
    exportBusy = true;
    backtestError.set(null);
    try {
      const { blob, filename } = await api.exportBacktestData($selectedRunId, $selectedOrgId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Deferred revoke (Safari cancels downloads whose blob URL is revoked synchronously) — mirrors the live export.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      console.error('Backtest export failed:', err);
      backtestError.set('Export failed — please try again.');
    } finally {
      exportBusy = false;
    }
  }

  function resetDefaults() {
    backtestResult.set(null);
    backtestError.set(null);
    selectedRunId.set(null);
    loadingRun.set(null);
    runSummary = null;
    lastParams = null;
    formComponent?.resetToDefaults();
  }

  async function loadRun(event) {
    // Ignore history selection while a run is in flight — the live result would otherwise clobber the
    // loaded one on completion (and backtestStatus keeps the view in 'running' until then anyway).
    if ($backtestRunning) return;

    // Instant selection + skeletons, then the heavy read with a stale-response guard — the sequencing lives in
    // loadRunInto (unit-tested); here we just supply the stores + api and the on-result summary update.
    const summary = event.detail.summary ?? null;
    await loadRunInto(
      { runId: event.detail.runId, summary, orgId: $selectedOrgId },
      {
        stores: { backtestResult, backtestError, selectedRunId, loadingRun },
        getRun: (id, orgId) => api.getBacktestRun(id, orgId),
        // Keep the loaded run's real strategy + full symbols in the collapsed summary (from the history row) —
        // richer than the generic "Saved run" + first-trade symbol the header used to fall back to.
        onResult: (result) => {
          runSummary = result
            ? { strategy: summary?.strategy ?? 'Saved run', symbols: summary?.symbols ?? (result.trades?.[0]?.symbol ?? '') }
            : null;
        },
      },
    );
  }

  // The history list owns the delete (and optimistically drops the row). If the run that was open in the
  // results pane is the one deleted, clear the pane so it doesn't show a now-gone run.
  function onRunDeleted(event) {
    if ($selectedRunId === event.detail.runId) {
      backtestResult.set(null);
      selectedRunId.set(null);
      loadingRun.set(null);
      runSummary = null;
    }
  }

  function onDeleteError() {
    backtestError.set('Could not delete that run. Please try again.');
  }

  // Org-switch-mid-run warning (U7): don't silently drop or mislabel the in-flight result.
  let lastOrg = $selectedOrgId;
  $: if ($selectedOrgId !== lastOrg) {
    if ($backtestRunning && runStartOrgId && $selectedOrgId !== runStartOrgId) {
      orgSwitchWarning =
        "A backtest is running — switching org won't cancel it, but live progress will be lost. " +
        "The result will appear in your previous org's history.";
    }
    lastOrg = $selectedOrgId;
  }

  $: showResults = $backtestStatus === 'done' && $backtestResult;
  $: showProgress = $backtestStatus === 'running';
  // TASK-20260728: the instant loading overlay — a history run was clicked and its heavy result is still in
  // flight. Header + skeletons render from $loadingRun's stored fields. Suppressed once the result lands
  // (showResults) or while a live run is progressing.
  $: showLoadingRun = !!$loadingRun && !$backtestResult && !showProgress;
  // Frequency → a human label for the loading header (5m/1h/1d…), from the summary's FrequencySeconds.
  function freqLabel(sec) {
    if (!sec) return '';
    if (sec % 86400 === 0) return `${sec / 86400}d`;
    if (sec % 3600 === 0) return `${sec / 3600}h`;
    if (sec % 60 === 0) return `${sec / 60}m`;
    return `${sec}s`;
  }
</script>

<div class="backtest-shell" class:history-hidden={!historyVisible}>
  <div class="backtest-main">
    <div class="backtest-view-header">
      <h2 class="backtest-view-title">Backtesting</h2>
      <span class="badge badge-warning sim-badge" title="Simulation — no real orders are placed">SIM</span>
      {#if showResults}
        <button
          type="button"
          class="bt-header-icon bt-export-zip"
          title={meaningful ? 'Download this run as a ZIP (returns, trades, metrics)' : 'No completed run to export'}
          aria-label="Download backtest ZIP"
          disabled={exportBusy || !meaningful || !$selectedRunId}
          on:click={onDownloadBacktestZip}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <button
          type="button"
          class="bt-header-icon bt-new-backtest"
          title="New backtest"
          aria-label="New backtest"
          on:click={newBacktest}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round"/>
          </svg>
        </button>
      {/if}
      <button
        type="button"
        class="bt-header-icon bt-history-toggle"
        class:active={historyVisible}
        aria-pressed={historyVisible}
        title={historyVisible ? 'Hide run history' : 'Show run history'}
        aria-label={historyVisible ? 'Hide run history' : 'Show run history'}
        on:click={() => (historyVisible = !historyVisible)}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" fill="none" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 4v4h4" fill="none" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 7v5l3.5 2" fill="none" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    {#if $backtestError}
      <div class="alert alert-danger">
        {$backtestError}
        <button class="alert-close" on:click={() => backtestError.set(null)}>×</button>
      </div>
    {/if}

    {#if orgSwitchWarning}
      <div class="alert alert-warning">
        {orgSwitchWarning}
        <button class="alert-close" on:click={() => orgSwitchWarning = null}>×</button>
      </div>
    {/if}

    <!-- Form collapses to a compact summary when results show, so the user always sees what they ran.
         The form stays MOUNTED (hidden via CSS) so "New backtest" can prefill its previous params. -->
    {#if showResults && runSummary}
      <div class="backtest-run-summary">
        <div>
          <strong>{runSummary.strategy}</strong> · {runSummary.symbols}
          <span class="sim-note">Simulated — no real orders placed.</span>
        </div>
      </div>
    {/if}

    <!-- TASK-20260728: instant loading overlay for a clicked history run — the header renders from the row's
         already-loaded stored fields, with skeletons where the heavy result's charts/tables will land. -->
    {#if showLoadingRun}
      <div class="backtest-run-summary" aria-live="polite">
        <div>
          <!-- summary.strategy is the enum NAME string (as RunHistory renders it), not an id — show it directly. -->
          <strong>{$loadingRun.strategy ?? 'Saved run'}</strong>
          {#if $loadingRun.symbols}· {$loadingRun.symbols}{/if}
          {#if $loadingRun.frequencySeconds}· {freqLabel($loadingRun.frequencySeconds)}{/if}
          {#if $loadingRun.sharpeRatio != null}· Sharpe {Number($loadingRun.sharpeRatio).toFixed(2)}{/if}
          <span class="sim-note">Loading run…</span>
        </div>
      </div>
      <div class="backtest-loading" aria-busy="true" aria-label="Loading backtest run">
        <div class="at-skeleton-rows">
          <div class="at-skeleton-row"></div>
          <div class="at-skeleton-row" style="width: 82%"></div>
          <div class="at-skeleton-row" style="width: 64%"></div>
        </div>
        <div class="backtest-loading-chart at-skeleton-row"></div>
        <div class="backtest-loading-chart at-skeleton-row"></div>
      </div>
    {/if}

    <div class="backtest-config" class:hidden={showResults || showLoadingRun}>
      <BacktestForm
        bind:this={formComponent}
        running={$backtestRunning}
        serverError={$backtestError}
        on:run={handleRun}
        on:reset={resetDefaults}
      />
    </div>

    {#if showProgress}
      <BacktestProgress
        {channelId}
        waiting={queuedWaiting}
        strategyName={runSummary?.strategy ?? ''}
        symbols={runSummary?.symbols ?? ''}
        on:terminalFailed={handleTerminalFailed}
      />
    {/if}

    {#if showResults}
      <BacktestResults result={$backtestResult} />
      <!-- Collapsed-by-default panel of the exact parameters this run used (live + historical). -->
      <RunParametersPanel parameters={$backtestResult.parameters} />
      {#if meaningful}
        <div class="backtest-charts">
          <EquityCurveChart data={$backtestResult.equityCurve} trades={$backtestResult.trades} />
          <DrawdownChart data={$backtestResult.equityCurve} />
          <ReturnsChart data={$backtestResult.equityCurve} />
        </div>
        <!-- Tear-sheet charts (U8/U9) — AFTER the existing 3, rendered reactively when the dataset is
             present. Keyed on runId so a new run swaps the whole section (no stale charts). -->
        {#if tearsheet}
          {#key $selectedRunId}
            <div class="backtest-charts backtest-charts-tearsheet">
              <RollingVolatilityChart data={tearsheet.rollingVolatility} />
              <RollingSharpeChart data={tearsheet.rollingSharpe} />
              <UnderwaterChart data={tearsheet.underwater} />
              <CumulativeVsBenchmarkChart data={tearsheet.cumulativeVsBenchmark} />
              <AnnualReturnsChart data={tearsheet.annualReturns} />
              <MonthlyReturnsHeatmap data={tearsheet.monthlyHeatmap} />
              <ReturnDistributionChart data={tearsheet.returnDistribution} />
              <ReturnQuantilesChart data={tearsheet.returnQuantiles} />
              <DrawdownPeriodsTable data={tearsheet.drawdownPeriods} />
            </div>
          {/key}
        {/if}
      {/if}
    {/if}
  </div>

  {#if historyVisible}
    <aside class="backtest-history">
      <RunHistory
        bind:this={historyComponent}
        on:select={loadRun}
        on:deleted={onRunDeleted}
        on:deleteError={onDeleteError}
      />
    </aside>
  {/if}
</div>

<style>
  @import './Backtesting.css';
</style>
